/**
 * Public support page (no auth) — required by Google Play + App Store.
 * Route: /support
 */
import { Link } from "react-router-dom";
import { Mail, HelpCircle, ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";

const faqs = [
  {
    q: "How do I get a new daily card?",
    a: "Open the Today tab — a card is drawn automatically each day. If nothing shows, tap Draw today's card. The next card unlocks after midnight in your local timezone.",
  },
  {
    q: "How do I use Ask Think, the AI helper?",
    a: "On any daily card or personal note, tap Ask Think. A side panel opens where you can chat with Think about that specific concept. Think never lectures — it asks questions that unlock your own thinking.",
  },
  {
    q: "Are my notes private?",
    a: "Yes. Only you can see your notes. They are stored encrypted on our database and are never used to train third-party AI models. Read the full details on our Privacy Policy.",
  },
  {
    q: "How do I delete my account?",
    a: "Email us at chaudharyhardik1106@gmail.com from your registered email address. We will delete your account and all associated content within 30 days.",
  },
  {
    q: "I forgot my password.",
    a: "Sign in with Google if you originally signed up that way. If you used email/password, please email us at chaudharyhardik1106@gmail.com and we'll help reset it. (A self-serve password-reset flow is coming soon.)",
  },
  {
    q: "Can I export my notes?",
    a: "Yes — email chaudharyhardik1106@gmail.com and we'll send you a machine-readable file of all your data within 30 days.",
  },
  {
    q: "Is there a mobile app?",
    a: "InThink works beautifully on any modern mobile browser. Native Android and iOS apps are coming soon.",
  },
  {
    q: "Something's broken. How do I report it?",
    a: "Email chaudharyhardik1106@gmail.com with a short description and (if possible) a screenshot. We usually reply within one business day.",
  },
];

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-[var(--inmind-bg)] text-[var(--inmind-text)]" data-testid="support-page">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="support-home-link">
            <Logo className="w-6 h-6" />
            <span className="font-serif text-lg text-[var(--inmind-text)]">InThink</span>
          </Link>
          <Link to="/privacy" className="text-sm text-[var(--inmind-primary)] hover:underline">Privacy</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">Help &amp; support</div>
        <h1 className="font-serif text-3xl sm:text-4xl mt-2 tracking-tight">How can we help?</h1>
        <p className="mt-3 text-[15px] text-[var(--inmind-text-secondary)] leading-relaxed max-w-xl">
          InThink is a calm daily-learning companion — one thoughtful concept a day, a private
          notebook, and an AI thinking partner. If something isn&apos;t working or you have a
          question, we&apos;re here.
        </p>

        <a
          href="mailto:chaudharyhardik1106@gmail.com?subject=InThink%20Support"
          data-testid="support-email-cta"
          className="mt-8 inline-flex items-center gap-2 rounded-full h-11 px-5 bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors font-medium"
        >
          <Mail className="w-4 h-4" strokeWidth={1.9} />
          Email support
        </a>
        <p className="mt-2 text-sm text-[var(--inmind-muted)]">
          Direct: <a href="mailto:chaudharyhardik1106@gmail.com" className="text-[var(--inmind-primary)] underline">chaudharyhardik1106@gmail.com</a> · we usually reply within one business day.
        </p>

        <section className="mt-14">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
            <HelpCircle className="w-3.5 h-3.5" strokeWidth={1.8} />
            Frequently asked
          </div>
          <h2 className="font-serif text-2xl mt-2 mb-6 tracking-tight">Common questions</h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details
                key={i}
                data-testid={`faq-${i}`}
                className="group bg-white rounded-xl border border-black/5 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.06)] overflow-hidden"
              >
                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 text-[15px] font-medium text-[var(--inmind-text)] hover:bg-black/5 transition-colors">
                  <span>{f.q}</span>
                  <span className="text-[var(--inmind-muted)] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-5 text-[15px] leading-relaxed text-[var(--inmind-text-secondary)]">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-2xl bg-white border border-black/5 p-6 sm:p-7">
          <h2 className="font-serif text-xl tracking-tight">Contact details</h2>
          <dl className="mt-4 text-[15px] text-[var(--inmind-text-secondary)] space-y-2">
            <div className="flex flex-wrap gap-x-3">
              <dt className="font-medium text-[var(--inmind-text)] w-28">Publisher</dt>
              <dd>Hardik Chaudhary</dd>
            </div>
            <div className="flex flex-wrap gap-x-3">
              <dt className="font-medium text-[var(--inmind-text)] w-28">App</dt>
              <dd>InThink</dd>
            </div>
            <div className="flex flex-wrap gap-x-3">
              <dt className="font-medium text-[var(--inmind-text)] w-28">Email</dt>
              <dd>
                <a href="mailto:chaudharyhardik1106@gmail.com" className="text-[var(--inmind-primary)] underline">
                  chaudharyhardik1106@gmail.com
                </a>
              </dd>
            </div>
            <div className="flex flex-wrap gap-x-3">
              <dt className="font-medium text-[var(--inmind-text)] w-28">Website</dt>
              <dd>
                <a href="https://inthink.world" className="text-[var(--inmind-primary)] underline inline-flex items-center gap-1">
                  inthink.world <ExternalLink className="w-3 h-3" />
                </a>
              </dd>
            </div>
            <div className="flex flex-wrap gap-x-3">
              <dt className="font-medium text-[var(--inmind-text)] w-28">Response time</dt>
              <dd>Usually within one business day</dd>
            </div>
          </dl>
        </section>
      </main>

      <footer className="border-t border-black/5 mt-12 py-8">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-3 text-sm text-[var(--inmind-muted)]">
          <span>© {new Date().getFullYear()} InThink</span>
          <div className="flex items-center gap-4">
            <Link to="/support" className="hover:text-[var(--inmind-text)]">Support</Link>
            <Link to="/privacy" className="hover:text-[var(--inmind-text)]">Privacy</Link>
            <Link to="/" className="hover:text-[var(--inmind-text)]">Open app</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
